import React, { useState, useEffect, useRef } from "react";
import { FaTimes, FaSave, FaCamera, FaUpload } from "react-icons/fa";
import axiosAuth from "../../utils/axios";
import toast from "react-hot-toast";

const ProviderProfileEdit = ({
  profile,
  onSave,
  onCancel,
  isLoading,
  error,
}) => {
  const fileInputRef = useRef(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [formData, setFormData] = useState({
    full_name: "",
    city: "",
    country: "",
    role: "",
    facility: "",
    facility_phone_number: "",
  });

  useEffect(() => {
    if (profile) {
      setFormData({
        full_name: profile.full_name || profile.user?.full_name || "",
        city: profile.city || profile.user?.city || "",
        country: profile.country || profile.user?.country || "",
        role: profile.role || profile.user?.role || "",
        facility: profile.facility || profile.user?.facility || "",
        facility_phone_number: profile.facility_phone_number || profile.user?.facility_phone_number || "",
      });
    }
  }, [profile]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleImageUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }

    setUploadingImage(true);
    try {
      const axiosInstance = axiosAuth();
      const formDataToSend = new FormData();
      formDataToSend.append('image', file);

      const response = await axiosInstance.patch('/provider/profile/', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('Profile picture uploaded successfully!');
      
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (err) {
      console.error('Failed to upload profile picture:', err);
      const errorMessage = err.response?.data?.detail || err.response?.data?.error || 'Failed to upload profile picture. Please try again.';
      toast.error(errorMessage);
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const inputFields = [
    { label: "Full Name", name: "full_name", placeholder: "Enter your full name" },
    { label: "City", name: "city", placeholder: "Enter your city" },
    { label: "Country", name: "country", placeholder: "Enter your country" },
    { label: "Role", name: "role", placeholder: "e.g., Physician, Nurse, etc." },
    { label: "Facility", name: "facility", placeholder: "Enter facility name" },
    { label: "Facility Phone", name: "facility_phone_number", placeholder: "(123) 456-7890" },
  ];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full mx-auto overflow-hidden border border-gray-200 dark:border-gray-700">
      
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-500 to-teal-600 px-6 py-5 flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">Edit Profile</h2>
        <button
          onClick={onCancel}
          className="p-2 hover:bg-white/20 rounded-lg transition-colors duration-200"
          type="button"
        >
          <FaTimes className="text-white text-xl" />
        </button>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="p-6 space-y-5">
        
        {/* Error Message */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {/* Profile Picture Upload Section */}
        <div className="flex flex-col items-center pb-5 border-b border-gray-200 dark:border-gray-700">
          <div className="relative mb-4">
            <img
              src={
                profile?.image && profile.image.startsWith("http")
                  ? profile.image
                  : profile?.image 
                    ? `${process.env.REACT_APP_MEDIA_URL || ''}${profile.image}` 
                    : "https://promedheatlhdatastorage.blob.core.windows.net/static/images/default_user.jpg"
              }
              alt="Profile"
              className="w-24 h-24 rounded-xl border-2 border-gray-300 dark:border-gray-600 object-cover shadow-md"
            />
            {uploadingImage && (
              <div className="absolute inset-0 bg-black/50 rounded-xl flex items-center justify-center">
                <svg className="animate-spin h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            )}
          </div>
          
          {/* Hidden File Input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageFileChange}
            className="hidden"
            disabled={uploadingImage}
          />

          {/* Upload Button */}
          <button
            type="button"
            onClick={handleImageUploadClick}
            disabled={uploadingImage}
            className="px-4 py-2.5 text-sm font-semibold text-white bg-teal-500 hover:bg-teal-600 rounded-xl shadow-sm hover:shadow transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {uploadingImage ? (
              <>
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Uploading...</span>
              </>
            ) : (
              <>
                <FaCamera className="text-sm" />
                <span>Upload Profile Picture</span>
              </>
            )}
          </button>
        </div>

        {/* Input Fields */}
        <div className="space-y-4">
          {inputFields.map(({ label, name, placeholder }) => (
            <div key={name} className="space-y-1.5">
              <label 
                htmlFor={name}
                className="block text-sm font-semibold text-gray-700 dark:text-gray-300"
              >
                {label}
              </label>
              <input
                id={name}
                name={name}
                type="text"
                value={formData[name]}
                onChange={handleChange}
                placeholder={placeholder}
                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200"
              />
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-xl transition-all duration-200"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="px-6 py-2.5 text-sm font-semibold text-white bg-teal-500 hover:bg-teal-600 rounded-xl shadow-sm hover:shadow transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 min-w-[120px] justify-center"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <FaSave />
                <span>Save Changes</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProviderProfileEdit;